/**
 * WorkflowStepFailures - Falhas de ação dentro de uma etapa do fluxo (BR-WF-OS-002).
 *
 * Mostra a mensagem do servidor e, para quem pode editar processos, o botão
 * "Tentar novamente", que reutiliza o reprocessamento oficial da fila de erros
 * (`resolve_workflow_dead_letter`, auditado no banco).
 *
 * CHANGELOG:
 *   RELEASE: 2026-10-17 v1.0.0 - Versão inicial.
 */
import { RotateCcw, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { usePermissions } from '@/hooks/usePermissions';
import { useWorkflowDeadLetters } from '@/hooks/useWorkflowDeadLetters';
import type { AcaoExecutada } from '@/lib/workflow/stepActionFailures';

export function WorkflowStepFailures({ instanceId, falhas }: { instanceId: string | null; falhas: AcaoExecutada[] }) {
  const { canEdit } = usePermissions();
  const podeReprocessar = canEdit('workflow_processes');
  // Fila de erros abertos; o item é casado pelo processo e pelo código da ação
  const { itens, resolve } = useWorkflowDeadLetters('aberto', { limit: 100 });
  if (!falhas.length) return null;

  return (
    <div className="mt-3 space-y-2 border-t pt-3">
      {falhas.map((f, i) => {
        const item = itens.find((d) => d.instance_id === instanceId && d.action_code === f.codigo);
        return (
          <div key={`${f.codigo}-${i}`} className="flex items-start gap-2 border-l-2 border-destructive pl-3">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-destructive" />
            <div className="min-w-0 flex-1">
              <p className="text-[11px] font-medium text-foreground">{f.nome ?? f.codigo} não foi executada</p>
              <p className="text-[10px] text-destructive">{f.erro ?? 'Sem detalhe do erro.'}</p>
            </div>
            {podeReprocessar && item && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-6 gap-1 px-2 text-[10px]"
                disabled={resolve.isPending}
                onClick={() => resolve.mutate({ id: item.id, acao: 'reprocessar', justificativa: 'Reprocessado pelo andamento do fluxo no registro' })}
              >
                <RotateCcw className="h-3 w-3" /> Tentar novamente
              </Button>
            )}
          </div>
        );
      })}
    </div>
  );
}
